import type React from "react";
import { useMemo } from "react";

import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useGetClinicPartnerActivePatients } from "api/services/clinic_partner/hooks";
import { NoGraphData } from "routes/admin/components/NoGraphData";
import { fillMissingMonths, getStartTimeSixMonthsAgo } from "routes/admin/components/utils";
import type { BarConfig } from "shared/atoms/Charts";
import { StackedBarChart } from "shared/atoms/Charts";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout";
import { WidgetBox } from "shared/containers/WidgetBox";

export const ClinicPartnerActivePatients: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: activePatientsData, isLoading } = useGetClinicPartnerActivePatients({
    params: {
      start_time: getStartTimeSixMonthsAgo(),
    },
  });
  const data = fillMissingMonths(activePatientsData?.items || [], "total");

  const hasData = data.some(item => (item.total || 0) > 0);

  const stackTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    data.forEach(item => {
      totals[item.month] = item.total || 0;
    });
    return totals;
  }, [data]);

  const bars: BarConfig[] = [
    { dataKey: "total", name: t("partner_admin.active_patients.bar_label"), color: theme.colors.redesign.b100 },
  ];

  return (
    <WidgetBox
      title={t("partner_admin.active_patients.title")}
      description={t("partner_admin.active_patients.description")}
      icon={<FontAwesomeIcon icon={faUsers} />}
      color="blue"
      skipPadding
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton />}
        {!isLoading && !hasData && <NoGraphData title={t("partner_admin.active_patients.no_data")} />}
        {!isLoading && hasData && (
          <StackedBarChart
            data={data}
            bars={bars}
            tooltipProps={{
              color: theme.colors.redesign.b100,
              labelFormatter: (label: string) => format(parseISO(label), "MMMM yyyy"),
              stackTotals,
            }}
            height={240}
            xAxisDataKey="month"
            xAxisProps={{
              tickFormatter: value => format(parseISO(value), "MMM"),
            }}
            hideLegend
          />
        )}
      </Box>
    </WidgetBox>
  );
};
